/**
 * Mission Watch — full-page loading screen.
 *
 * Shown BEFORE any protected page renders its content, so the customer never
 * sees a "flash" of placeholder state (like "0 of 4 done" or "Your Organization")
 * while the dashboard's primary APIs are still loading.
 *
 * Design: dark background matching the marketing site + Mission Watch shield
 * lockup + subtle animated progress bar. Renders synchronously on the very
 * first paint — no delayed timers, no overlays.
 *
 * Usage pattern (bulletproof): call this as an *early return* while the
 * page's initial data is still in flight. Do NOT render this as an overlay
 * on top of the real content — that lets the placeholder DOM construct
 * underneath and causes the exact flash we're trying to prevent.
 *
 *   if (initialLoading) return <LoadingScreen />;
 *   return <ActualPage ... />;
 */
export function LoadingScreen({ label = "Watching over your organization…" }) {
  return (
    <div
      className="fixed inset-0 z-[9999] bg-charcoal-900 flex items-center justify-center"
      data-testid="loading-screen"
      aria-live="polite"
      aria-busy="true"
    >
      <div className="flex flex-col items-center gap-6">
        {/* Logo mark — pulses gently */}
        <div className="relative">
          <div className="absolute inset-0 rounded-2xl bg-emerald-400/30 blur-2xl animate-pulse" />
          <div className="relative w-16 h-16 rounded-2xl bg-charcoal-900 border border-white/5 grid place-items-center shadow-2xl">
            <svg width="30" height="30" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M12 2L4 5v6c0 5 3.4 9.4 8 11 4.6-1.6 8-6 8-11V5l-8-3z"
                stroke="#34D399"
                strokeWidth="1.6"
                strokeLinejoin="round"
                fill="rgba(52,211,153,0.08)"
              />
              <path
                d="M9 12l2 2 4-4"
                stroke="#34D399"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
        </div>

        {/* Wordmark */}
        <div className="text-center">
          <p className="font-mono text-[11px] uppercase tracking-[0.28em] text-white/40 mb-1.5">
            Mission Watch
          </p>
          <p className="text-sm text-white/60">{label}</p>
        </div>

        {/* Thin progress bar for motion feedback */}
        <div className="relative w-40 h-0.5 bg-white/5 rounded-full overflow-hidden">
          <div className="absolute inset-y-0 left-0 w-1/3 bg-emerald-400 rounded-full animate-loading-slide" />
        </div>
      </div>

      {/* Local keyframes — kept inline so the loading screen never depends
          on external CSS that might itself be loading. */}
      <style>{`
        @keyframes loading-slide {
          0%   { transform: translateX(-100%); }
          50%  { transform: translateX(200%); }
          100% { transform: translateX(400%); }
        }
        .animate-loading-slide {
          animation: loading-slide 1.4s cubic-bezier(0.4, 0, 0.2, 1) infinite;
        }
      `}</style>
    </div>
  );
}
